import { useState } from 'react';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setSubmitStatus('success');
      setFormMessage('Thank you for subscribing!');
      setEmail('');
      setTimeout(() => { setSubmitStatus('idle'); setFormMessage(''); }, 4000);
      return;
    }

    setSubmitStatus('submitting');
    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d96asf70d76aer3t5260', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setSubmitStatus('success');
        setFormMessage('Thank you for subscribing! Check your inbox for updates.');
        setEmail('');
      } else {
        const serverMsg = (parsed && typeof (parsed as Record<string, unknown>).meta === 'object' && (parsed as Record<string, unknown>).meta)
          ? ((parsed as Record<string, unknown>).meta as Record<string, string>).message || ''
          : '';
        setSubmitStatus('error');
        setFormMessage(serverMsg || 'Something went wrong. Please try again.');
      }
    } catch {
      setSubmitStatus('error');
      setFormMessage('Network error. Please try again.');
    }
  };

  return (
    <footer className="bg-background-100 border-t border-background-200/60">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-14 md:py-18">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div className="lg:col-span-1">
            <h3 className="font-heading text-xl md:text-2xl text-foreground-900 font-semibold mb-3">
              Join the<br />Global Shoppers
            </h3>
            <p className="text-foreground-500 text-sm mb-5 leading-relaxed">
              Get weekly spreadsheet updates, new verified finds, and exclusive deals delivered to your inbox.
            </p>
            <form onSubmit={handleSubscribe} data-readdy-form>
              <div className="form-honeypot-wrap">
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="flex-1 px-4 py-2.5 bg-white border border-background-300 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 transition-colors"
                />
                <button
                  type="submit"
                  disabled={submitStatus === 'submitting'}
                  className="px-5 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
                >
                  {submitStatus === 'submitting' ? 'Sending...' : 'Subscribe'}
                </button>
              </div>
              {formMessage && (
                <p className={`mt-2 text-xs ${submitStatus === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                  {formMessage}
                </p>
              )}
            </form>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-foreground-900 mb-4 whitespace-nowrap">SEO Guides</h4>
            <ul className="space-y-2.5">
              <li><a href="/article/cssbuy-spreadsheet-guide" className="text-sm text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap">Ultimate Guide</a></li>
              <li><a href="/article/cssbuy-spreadsheet-guide" className="text-sm text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap">Shipping Guide</a></li>
              <li><a href="/article/cssbuy-spreadsheet-guide" className="text-sm text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap">QC Guide</a></li>
              <li><a href="/article/cssbuy-spreadsheet-guide" className="text-sm text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap">Brand Reviews</a></li>
              <li><a href="/article/cssbuy-spreadsheet-guide" className="text-sm text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap">Updates</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-foreground-900 mb-4 whitespace-nowrap">Top Brands</h4>
            <ul className="space-y-2.5">
              <li><a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-sm text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap">Nike</a></li>
              <li><a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-sm text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap">Adidas</a></li>
              <li><a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-sm text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap">Jordan</a></li>
              <li><a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-sm text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap">Supreme</a></li>
              <li><a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-sm text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap">Stussy</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-foreground-900 mb-4 whitespace-nowrap">Quick Shop</h4>
            <ul className="space-y-2.5">
              {categories.slice(0, 6).map((cat) => (
                <li key={cat.name}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-background-200/60 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-foreground-400">
            &copy; {new Date().getFullYear()} cssspreadsheet.bond — Your trusted CSSBuy Spreadsheet resource. Not affiliated with CSSBuy.
          </p>
          <div className="flex items-center gap-4">
            <a href="/" className="text-xs text-foreground-400 hover:text-foreground-600 transition-colors whitespace-nowrap">Home</a>
            <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-xs text-foreground-400 hover:text-primary-600 transition-colors whitespace-nowrap">Main Store</a>
          </div>
        </div>
      </div>
    </footer>
  );
}